import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { assetsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { AssetApiResponse } from '../../app/modules/types/asset'
import type { OptionsType } from '../../app/modules/types/filters'

/**
 * Hook for fetching paginated assets/domains list
 * Automatically refetches when URL params change
 */
export function useAssets() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.assets.list(orgId, params),
    queryFn: () => assetsApi.getList(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching asset filter options
 * Cached for 15 minutes as these change less frequently
 */
export function useAssetFilterOptions() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.assets.filterOptions(orgId),
    queryFn: () => assetsApi.getFilterOptions(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a single asset by ID
 * Optionally accepts custom_scan_ids parameter for historical data
 */
export function useAssetDetail(id: string | null, customScanIds?: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.assets.detail(orgId, id || '', customScanIds),
    queryFn: () => assetsApi.getById(id!, customScanIds),
    enabled: !!id && !!orgId,
  })
}
